import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const KEY = 'studio-basket';

interface BasketState {
  ids: string[];
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  add: (id: string) => void;
  remove: (id: string) => void;
  clear: () => void;
  reorder: (ids: string[]) => void;
  /** the picks drawer, which every page can open */
  trayOpen: boolean;
  setTrayOpen: (open: boolean) => void;
}

const BasketContext = createContext<BasketState | null>(null);

/**
 * A staging area for exercises picked while browsing.  Kept in localStorage so
 * a reload — or a detour into another sheet — does not lose the selection.
 */
export function BasketProvider({ children }: { children: ReactNode }) {
  const [ids, setIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(ids));
    } catch {
      /* a private window may refuse storage; the picks then last for this tab */
    }
  }, [ids]);

  const [trayOpen, setTrayOpen] = useState(false);
  const add = useCallback((id: string) => setIds((prev) => (prev.includes(id) ? prev : [...prev, id])), []);
  const remove = useCallback((id: string) => setIds((prev) => prev.filter((x) => x !== id)), []);

  const value = useMemo<BasketState>(
    () => ({
      ids,
      has: (id) => ids.includes(id),
      toggle: (id) => setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
      add,
      remove,
      clear: () => setIds([]),
      reorder: setIds,
      trayOpen,
      setTrayOpen,
    }),
    [ids, add, remove, trayOpen],
  );

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
}

export function useBasket(): BasketState {
  const ctx = useContext(BasketContext);
  if (!ctx) throw new Error('useBasket outside BasketProvider');
  return ctx;
}
